import { useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { motion, AnimatePresence } from "../components/motion";
import { useContent } from "../context/SiteContentContext";

const buildContactItems = (get) => [
  { Icon: Phone, label: "Phone", value: get("site.phone"), sub: "Call or WhatsApp, anytime", href: `tel:${get("site.phone_link")}` },
  { Icon: Mail, label: "Email", value: get("site.email"), sub: "We respond within 24 hours", href: `mailto:${get("site.email")}` },
  { Icon: MapPin, label: "Location", value: get("site.location_short"), sub: get("site.location_note"), href: null },
  { Icon: Clock, label: "Availability", value: get("site.hours"), sub: "Round-the-clock concierge support", href: null },
];

/* Floating-label field wrapper */
function Field({ label, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 block">
        {label}
      </Label>
      {children}
    </motion.div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { get } = useContent();
  const CONTACT_ITEMS = buildContactItems(get);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-1 h-12 w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 text-white placeholder:text-white/20 focus:border-gold focus:ring-0 transition-colors duration-300 text-sm";
  const textareaCls =
    "mt-1 w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 text-white placeholder:text-white/20 focus:border-gold focus:ring-0 transition-colors duration-300 text-sm resize-none";

  return (
    <div className="grain min-h-screen" style={{ background: "var(--bg-base)" }} data-testid="contact-page">

      {/* ── Cinematic header ── */}
      <div className="relative overflow-hidden" style={{ paddingTop: "10rem", paddingBottom: "6rem" }}>
        {/* Background glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: 0, left: "50%", transform: "translateX(-50%)",
            width: 700, height: 400,
            background: "radial-gradient(ellipse, rgba(201,162,75,0.07) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {/* Eyebrow */}
            <motion.div
              className="eyebrow"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              Concierge
            </motion.div>

            {/* Headline */}
            <div style={{ overflow: "hidden" }}>
              <motion.h1
                className="font-cormorant text-white mt-4 leading-none"
                style={{ fontSize: "clamp(3.5rem, 8vw, 6rem)" }}
                initial={{ y: "100%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.85, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                Get in touch.
              </motion.h1>
            </div>

            {/* Gold rule */}
            <motion.div
              className="h-px mt-6 mb-6"
              style={{ background: "var(--gold)", transformOrigin: "left" }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />

            {/* Subtext */}
            <motion.p
              className="text-white/45 text-sm leading-relaxed tracking-wide"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              Discreet support, 24 hours a day. Whether you have a question about
              availability, a special request, or need directions — we're here.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* ── Main two-column grid ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
        <div className="grid lg:grid-cols-5 gap-16 lg:gap-24">

          {/* ── Left column: contact info ── */}
          <div className="lg:col-span-2 space-y-3">
            {CONTACT_ITEMS.map((item, i) => (
              <motion.div
                key={item.label}
                className="group flex gap-5 items-start p-5 border border-white/[0.04] hover:border-gold/20 transition-colors duration-400"
                style={{ background: "rgba(20,17,14,0.45)" }}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Icon box */}
                <div
                  className="shrink-0 flex items-center justify-center border border-gold/25 group-hover:border-gold/50 transition-colors duration-400"
                  style={{ width: 44, height: 44 }}
                >
                  <item.Icon size={15} className="text-gold" />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/35 mb-1">
                    {item.label}
                  </div>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-white text-sm hover:text-gold transition-colors duration-300 block truncate"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div className="text-white text-sm">{item.value}</div>
                  )}
                  <div className="text-white/35 text-xs mt-1">{item.sub}</div>
                </div>

                {item.href && (
                  <ArrowRight size={13} className="text-white/20 group-hover:text-gold shrink-0 mt-1 transition-colors duration-300" />
                )}
              </motion.div>
            ))}

            {/* WhatsApp CTA */}
            <motion.a
              href={`https://wa.me/${get("site.whatsapp")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold w-full mt-6 flex items-center justify-center gap-2"
              data-testid="whatsapp-contact-btn"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <MessageCircle size={15} />
              Chat on WhatsApp
            </motion.a>

            {/* Map */}
            <motion.div
              className="overflow-hidden mt-4"
              style={{ height: 240, border: "1px solid rgba(255,255,255,0.05)" }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <iframe
                title="map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(get("site.map_query"))}&t=&z=15&ie=UTF8&iwloc=B&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.7)" }}
                allowFullScreen=""
                loading="lazy"
              />
            </motion.div>
          </div>

          {/* ── Right column: form ── */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mb-10"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-3">Send a message</div>
              <p className="text-white/35 text-sm leading-relaxed">
                Fill in the form below and a member of our team will reach out shortly.
              </p>
            </motion.div>

            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              {/* Name + Phone */}
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="Full name" delay={0.1}>
                  <Input
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                    data-testid="contact-form-name"
                  />
                </Field>
                <Field label="Phone (optional)" delay={0.15}>
                  <Input
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls}
                    data-testid="contact-form-phone"
                  />
                </Field>
              </div>

              {/* Email */}
              <Field label="Email address" delay={0.2}>
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                  data-testid="contact-form-email"
                />
              </Field>

              {/* Subject */}
              <Field label="Subject" delay={0.25}>
                <Input
                  required
                  placeholder="What is this regarding?"
                  value={form.subject}
                  onChange={set("subject")}
                  className={inputCls}
                  data-testid="contact-form-subject"
                />
              </Field>

              {/* Message */}
              <Field label="Message" delay={0.3}>
                <Textarea
                  required
                  rows={5}
                  placeholder="Tell us how we can help…"
                  value={form.message}
                  onChange={set("message")}
                  className={textareaCls}
                  data-testid="contact-form-message"
                />
              </Field>

              {/* Divider */}
              <motion.div
                className="h-px"
                style={{ background: "rgba(255,255,255,0.06)" }}
                initial={{ scaleX: 0, transformOrigin: "left" }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.6, delay: 0.35 }}
              />

              {/* Submit */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{ duration: 0.5, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-6"
              >
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold flex items-center gap-2 px-10"
                  data-testid="contact-form-submit"
                  style={{
                    background: loading
                      ? "rgba(201,162,75,0.5)"
                      : "linear-gradient(135deg, var(--gold) 0%, var(--gold-deep) 100%)",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {loading ? (
                      <motion.span
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <span className="inline-block w-3 h-3 border border-black/40 border-t-black rounded-full animate-spin" />
                        Sending…
                      </motion.span>
                    ) : sent ? (
                      <motion.span
                        key="sent"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        Message sent
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        Send Message <Send size={13} />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>

                <span className="text-white/25 text-xs">
                  Confidential · Discreet
                </span>
              </motion.div>
            </form>
          </div>
        </div>
      </div>

      {/* ── Bottom gold strip ── */}
      <motion.div
        className="max-w-7xl mx-auto px-6 lg:px-12 pb-20"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.8 }}
      >
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-6 px-10 py-7 border border-gold/15"
          style={{ background: "rgba(201,162,75,0.04)" }}
        >
          <div>
            <div className="font-cormorant text-gold text-xl">Need immediate assistance?</div>
            <div className="text-white/35 text-xs mt-1 tracking-wide">Our concierge team is on standby.</div>
          </div>
          <a
            href={`tel:${get("site.phone_link")}`}
            className="btn-gold flex items-center gap-2 whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, var(--gold) 0%, var(--gold-deep) 100%)" }}
          >
            <Phone size={13} /> Call Now
          </a>
        </div>
      </motion.div>
    </div>
  );
}
